import React, { useEffect, useRef, useState } from 'react';
import { X, ShieldCheck, ExternalLink, Clock, ArrowRight, Mail, MessageCircle, Loader2, Tag } from 'lucide-react';
import {
  CouponQuote,
  validateCoupon,
  normalizeCouponCode,
  rememberCoupon,
  readRememberedCoupon,
  forgetCoupon,
  couponReasonText,
  couponDurationText,
} from '@/lib/coupons';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  money,
  BRAND,
  planTermMonths,
  planTermNote,
  whatsappHref,
  SINGLE_LEAD_PRODUCT,
  BASIC_INTRO_OFFER,
  introOfferLine,
} from '@/data/constants';
import { track } from '@/lib/tracking';
import { useOverlayLock } from '@/lib/overlay';
import { LeadSettlement, VerificationGateCode, isMobileViewport } from '@/lib/singleLead';
import SingleLeadPayment from '@/components/SingleLeadPayment';

import { PaymentKind } from '@/lib/billing';
import { MarketplaceDisclaimer, AcceptTermsCheckbox } from '@/components/MarketplaceDisclaimer';
import { TERMS_VERSION, BILLING_POLICY_HEADING, CHECKOUT_BILLING_NOTE, bi } from '@/data/legal';


/**
 * Checkout modal — TWO PRODUCTS, TWO MECHANISMS, ON PURPOSE.
 *
 * MEMBERSHIPS (basic | pro | premium)
 *   Hosted checkout page. The platform never touches card data and the plan is
 *   fulfilled only when the signed payment-webhook arrives. Opening, or
 *   returning from, a checkout page activates nothing.
 *
 * $29 SINGLE LEAD (listingId present)
 *   <SingleLeadPayment/> asks the SERVER which checkout can take money right
 *   now (single-lead-purchase `route`, 2026-09-29 root-cause fix):
 *     inline  -> card form bound to THIS listing (unlocks it in one transaction)
 *     hosted  -> automatic redirect to the hosted $29 checkout; the webhook
 *                grants 1 credit and /payment-complete returns the buyer to
 *                THIS listing to unlock it with one tap
 *     contact -> nothing can take money; admins alerted, WhatsApp/email shown
 *   The buyer is never shown a payment form that cannot complete.
 *
 * MOBILE: `window.open()` after an await is blocked by Safari/Chrome on phones
 * (the user gesture has already ended), which made the Pay button look dead on
 * mobile while working on desktop. openCheckoutUrl() below opens the tab
 * synchronously and falls back to a same-tab navigation. The inline $29 form
 * opens no window at all.
 */



interface Props {
  open: boolean;
  onClose: () => void;
  amount: number;
  title: string;
  subtitle: string;
  lineItem: string;
  /** Plan code (basic | pro | premium) — required for subscription checkout. */
  planCode?: string;
  kind?: PaymentKind;
  /** Fired once the hosted checkout has been opened (never means "paid"). */
  onCheckoutOpened?: () => void;
  /** Optional alternative action, e.g. use an included subscription lead */
  altAction?: { label: string; run: () => Promise<void> | void };
  /** Optional informational note, e.g. "no included leads left this month" */
  note?: string;
  /** Where to send the user to pick a plan (lead flow with no allowance left). */
  plansHref?: string;
  /** Lead being purchased — enables the inline $29 card form. */
  listingId?: string;
  listingSlug?: string;
  /** Called after a $29 payment settles (credit issued, lead unlocked). */
  onLeadPaid?: (settlement: LeadSettlement) => void;
  /** Server refused the $29 purchase: account not verified (email/phone gate). */
  onVerificationRequired?: (code: VerificationGateCode) => void;
  /** Membership discount code to pre-apply (e.g. from ?coupon=PRO10). */
  initialCoupon?: string;
}

const CheckoutModal: React.FC<Props> = ({
  open,
  onClose,
  amount,
  title,
  subtitle,
  lineItem,
  planCode,
  kind = 'lead',
  onCheckoutOpened,
  altAction,
  note,
  plansHref,
  listingId,
  listingSlug,
  onLeadPaid,
  onVerificationRequired,
  initialCoupon,
}) => {
  const { t, lang, path } = useI18n();
  const navigate = useNavigate();
  const { user, profile } = useAuth();

  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  /** Last product code sent to checkout — used by "reopen checkout". */
  const [lastCode, setLastCode] = useState('');
  const submitting = useRef(false);

  /** Membership discount code (validated server-side). */
  const [couponInput, setCouponInput] = useState('');
  const [quote, setQuote] = useState<CouponQuote | null>(null);
  const [checkingCoupon, setCheckingCoupon] = useState(false);
  const [couponMsg, setCouponMsg] = useState('');

  // Register with the overlay registry so the floating Start Now / WhatsApp
  // stack steps aside (and page scroll locks) while checkout is open.
  useOverlayLock(open);

  const applyCoupon = async (raw?: string, silent = false) => {
    const code = normalizeCouponCode(raw ?? couponInput);
    if (!code || !planCode) return;
    setCouponInput(code);
    setCheckingCoupon(true);
    setCouponMsg('');
    const q = await validateCoupon(code, planCode, user?.id || profile?.id || null);
    setCheckingCoupon(false);
    if (q.valid) {
      setQuote(q);
      rememberCoupon(code);
      setCouponMsg(
        lang === 'es'
          ? `Cupón aplicado: ${money(q.discountCents || 0)} de descuento (${couponDurationText(q, lang)}).`
          : `Coupon applied: ${money(q.discountCents || 0)} off (${couponDurationText(q, lang)}).`
      );
      track('cta_click', { action: 'coupon_applied', plan: planCode, coupon: code });
    } else {
      setQuote(null);
      // The automatic intro offer is simply not shown to someone it does not
      // apply to (e.g. an existing member) — no red error for a code they never typed.
      if (silent) { setCouponInput(''); return; }
      setCouponMsg(couponReasonText(q.reason, lang));
    }
  };

  useEffect(() => {
    if (!open) return;
    setAck(false);
    setError('');
    setSent(false);
    setLastCode('');
    submitting.current = false;
    setQuote(null);
    setCouponMsg('');
    // A code from a shared link (?coupon=PRO10) is applied automatically.
    // Basic with no other code gets the intro offer ($50 first month) automatically.
    const shared = kind === 'subscription' && planCode ? normalizeCouponCode(initialCoupon || readRememberedCoupon()) : '';
    const intro = kind === 'subscription' && planCode === BASIC_INTRO_OFFER.plan && !shared ? BASIC_INTRO_OFFER.coupon : '';
    const preset = shared || intro;
    setCouponInput(preset);
    if (preset) applyCoupon(preset, !shared);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, planCode]);



  /** Store the buyer's acknowledgement of the marketplace disclaimer. */
  const logAck = async () => {
    await supabase.from('mkt_acknowledgements').insert({
      user_id: user?.id || null,
      email: profile?.email || user?.email || null,
      context: kind === 'subscription' ? 'checkout-subscription' : `checkout-${kind}`,
      terms_version: TERMS_VERSION,
      language: lang,
      detail: lineItem,
    });
  };

  /**
   * Record a checkout attempt that could not reach a payment page, so an
   * administrator can follow up (Admin > Payments). The edge function logs this
   * server-side too; `logged` tells us whether that already happened so we do
   * not create a duplicate row.
   */
  const logBlockedAttempt = async (planAttempted: string, reason: string, msg: string) => {
    try {
      await supabase.from('mkt_checkout_failures').insert({
        plan_code: planAttempted,
        user_id: user?.id || profile?.id || null,
        email: profile?.email || user?.email || null,
        name: profile?.company_name || profile?.contact_name || null,
        phone: profile?.phone || null,
        reason,
        message: msg,
        language: lang,
      });
    } catch (_e) {
      /* never block the UI on logging */
    }
  };

  /**
   * MOBILE POPUP FIX. `window.open` is only permitted inside the synchronous
   * part of a tap, so we open the tab immediately (about:blank) and point it at
   * the checkout url once it arrives. If the browser still refuses (iOS Safari
   * with "Block Pop-ups" on returns null), we navigate the current tab instead
   * of silently doing nothing.
   */
  const openCheckoutUrl = (url: string, placeholder: Window | null) => {
    if (placeholder && !placeholder.closed) {
      placeholder.location.href = url;
      return;
    }
    if (isMobileViewport()) {
      window.location.assign(url);
      return;
    }
    const tab = window.open(url, '_blank', 'noopener,noreferrer');
    if (!tab) window.location.assign(url);
  };

  /**
   * Fetch the hosted checkout link for a product and open it.
   *
   * ONE PATH FOR EVERY PRODUCT — memberships AND the $29 single lead. The code
   * is passed in ('basic' | 'pro' | 'premium' | 'single') rather than read from
   * `planCode`, because the single lead has no plan.
   *
   * `listing` travels with the request so the hosted checkout's return URL
   * brings the buyer back to the exact project they were looking at.
   */
  const goToCheckout = async (code?: string) => {
    const productCode = code || planCode;
    if (!ack || !productCode) return;
    // Synchronous re-entry guard: the first tap wins, repeats are ignored, so
    // repeated taps can never open two checkouts.
    if (submitting.current) return;
    submitting.current = true;
    setLastCode(productCode);

    // Reserve the tab INSIDE the gesture (desktop + Android). Mobile Safari
    // usually returns null here; openCheckoutUrl handles that.
    const placeholder = isMobileViewport() ? null : window.open('', '_blank');

    setBusy(true);
    setError('');
    track('cta_click', {
      action: productCode === SINGLE_LEAD_PRODUCT.code ? 'buy_single_lead' : 'plan_checkout',
      plan: productCode,
      listing: listingId || '',
    });
    await logAck();

    const { data, error: err } = await supabase.functions.invoke('checkout-link', {
      body: {
        plan: productCode,
        email: profile?.email || user?.email || '',
        name: profile?.company_name || profile?.contact_name || '',
        phone: profile?.phone || '',
        userId: user?.id || profile?.id || '',
        listing: listingSlug || listingId || '',
        language: lang,
        // Server re-validates; an invalid code never blocks checkout.
        coupon: kind === 'subscription' && quote?.valid ? quote.code : undefined,
      },
    });

    setBusy(false);
    submitting.current = false;

    if (err || !data?.configured || !data?.url) {
      if (placeholder && !placeholder.closed) placeholder.close();
      // The function returns a specific, human readable message when a product's
      // hosted checkout link has not been set up yet (e.g. "Premium checkout is
      // not configured yet"). Show it verbatim so the button is never a dead end.
      const msg = typeof data?.message === 'string' && data.message ? data.message : t('pay.setup');
      setError(msg);
      if (data?.logged !== true) {
        await logBlockedAttempt(productCode, data?.reason || (err ? 'error' : 'checkout_link_not_set'), msg);
      }
      return;
    }

    openCheckoutUrl(data.url, placeholder);
    setSent(true);
    onCheckoutOpened?.();
  };



  /** Prefilled support email used whenever checkout cannot be started. */
  const contactHref = `mailto:${BRAND.email}?subject=${encodeURIComponent(
    `${planCode ? `${planCode} plan` : 'Checkout'} — ${BRAND.name}`
  )}`;

  /**
   * The $29 inline card form is used whenever a SPECIFIC lead is being bought.
   * It is the only path that can bind the payment to the selected listing.
   */
  const canBuySingle = kind === 'lead' && !!listingId && !planCode;


  /**
   * After settlement: hand the result to the page (it refreshes entitlement and
   * reveals the contact block), then — if the payment UNLOCKED the lead and we
   * are not already on it — navigate straight to that lead using the slug the
   * server returned. The contractor never has to find the project again.
   */
  const handleSettled = (s: LeadSettlement) => {
    onLeadPaid?.(s);
    if (s.ok && s.unlocked) {
      const target = s.slug || listingSlug;
      if (target && target !== listingSlug) {
        onClose();
        navigate(path(`/jobs/${target}`));
      }
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-900/60 p-0 sm:items-center sm:p-4">
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-6 shadow-2xl sm:rounded-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-extrabold text-slate-900">{title}</h2>
            <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100" aria-label={t('common.close')}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-5 rounded-xl bg-slate-50 p-4">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{t('pay.summary')}</p>
          <div className="mt-2 flex items-center justify-between text-sm">
            <span className="text-slate-600">{lineItem}</span>
            <span className="font-semibold text-slate-900">{money(amount)}</span>
          </div>
          {quote?.valid && (
            <div className="mt-2 flex items-center justify-between text-sm">
              <span className="flex items-center gap-1.5 font-semibold text-emerald-700">
                <Tag className="h-3.5 w-3.5" /> {quote.code} · {couponDurationText(quote, lang)}
              </span>
              <span className="font-semibold text-emerald-700">−{money(quote.discountCents || 0)}</span>
            </div>
          )}
          <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-2 text-base">
            <span className="font-bold text-slate-900">{quote?.valid ? (lang === 'es' ? 'Total hoy' : 'Total today') : t('common.total')}</span>
            <span className="font-extrabold text-slate-900">{money(quote?.valid ? quote.finalPriceCents || amount : amount)}</span>
          </div>
          {quote?.valid && quote.duration === 'once' && (
            <p className={`mt-2 text-xs ${quote.introCredits ? 'rounded-lg bg-emerald-50 px-3 py-2 font-semibold text-emerald-900' : 'text-slate-500'}`}>
              {quote.introCredits
                ? introOfferLine(lang)
                : lang === 'es'
                ? `Después ${money(amount)}/mes. Recibe todos sus créditos de lead cada mes, con o sin cupón.`
                : `Then ${money(amount)}/mo. You get your full lead credits every month, coupon or not.`}
            </p>
          )}
          {planTermMonths(planCode) > 1 && (
            <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold leading-relaxed text-amber-900">
              {planTermNote(planCode, amount, lang)}
            </p>
          )}
        </div>

        {kind === 'subscription' && planCode && !sent && (
          <div className="mt-4">
            <label htmlFor="coupon-code" className="text-xs font-bold uppercase tracking-wide text-slate-500">
              {lang === 'es' ? 'Código de descuento' : 'Discount code'}
            </label>
            <div className="mt-1.5 flex gap-2">
              <input
                id="coupon-code"
                value={couponInput}
                onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); if (quote) setQuote(null); setCouponMsg(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyCoupon(); } }}
                placeholder={lang === 'es' ? 'Ej. PRO10' : 'e.g. PRO10'}
                autoComplete="off"
                className="min-w-0 flex-1 rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-semibold uppercase tracking-wide text-slate-900 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-200"
              />
              {quote?.valid ? (
                <button type="button" onClick={() => { setQuote(null); setCouponInput(''); setCouponMsg(''); forgetCoupon(); }}
                  className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50">
                  {lang === 'es' ? 'Quitar' : 'Remove'}
                </button>
              ) : (
                <button type="button" onClick={() => applyCoupon()} disabled={checkingCoupon || !couponInput.trim()}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50">
                  {checkingCoupon && <Loader2 className="h-4 w-4 animate-spin" />}
                  {lang === 'es' ? 'Aplicar' : 'Apply'}
                </button>
              )}
            </div>
            {couponMsg && (
              <p role="status" className={`mt-1.5 text-xs font-semibold ${quote?.valid ? 'text-emerald-700' : 'text-red-600'}`}>{couponMsg}</p>
            )}
            {quote?.valid && (
              <p className="mt-1 text-xs text-slate-500">
                {lang === 'es'
                  ? `Si la página de pago no muestra el descuento, escriba ${quote.code} en el campo de cupón antes de pagar.`
                  : `If the payment page does not show the discount yet, type ${quote.code} in its coupon field before paying.`}
              </p>
            )}
          </div>
        )}


        <p className="mt-4 rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs leading-relaxed text-slate-600">
          <span className="font-bold text-slate-900">{bi(BILLING_POLICY_HEADING, lang)}. </span>
          {bi(CHECKOUT_BILLING_NOTE, lang)}
        </p>

        <MarketplaceDisclaimer variant="short" className="mt-4" />
        <AcceptTermsCheckbox checked={ack} onChange={setAck} kind="checkout" className="mt-3" />


        {altAction && (
          <button
            onClick={async () => {
              if (!ack) return;
              setBusy(true);
              await logAck();
              await altAction.run();
              setBusy(false);
            }}
            disabled={busy || !ack}
            className="mt-4 w-full rounded-xl border-2 border-emerald-600 px-4 py-3 text-sm font-bold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
          >
            {altAction.label}
          </button>
        )}

        {note && <p className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">{note}</p>}

        <div className="mt-5">
          {canBuySingle ? (
            /* ───────────────────────────────────────────────────────────────
               $29 SINGLE LEAD — INLINE CARD FORM.
               The PaymentIntent is created for THIS listing, so a successful
               payment unlocks THIS lead. Nothing is charged until the terms
               box is ticked, and the form opens no new tab (mobile safe).
               ─────────────────────────────────────────────────────────────── */
            ack ? (
              <SingleLeadPayment
                listingId={listingId as string}
                slug={listingSlug || ''}
                onSettled={handleSettled}
                plansHref={plansHref}
                onLeavePage={onClose}
                onVerificationRequired={onVerificationRequired}
              />
            ) : (
              <p className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 text-center text-sm font-semibold text-slate-500">
                {t('pay.acceptToContinue')}
              </p>
            )
          ) : sent ? (
            <div className="rounded-xl bg-blue-50 px-4 py-4 text-sm text-blue-900">
              <p className="flex items-center gap-2 font-bold">
                <Clock className="h-4 w-4" /> {t('pay.pendingTitle')}
              </p>
              <p className="mt-1.5 leading-relaxed">{t('pay.pendingBody')}</p>
              <button onClick={() => goToCheckout(lastCode)} className="mt-3 text-sm font-bold text-blue-700 underline">
                {t('pay.reopenCheckout')}
              </button>
            </div>
          ) : planCode ? (
            <>
              {error && (
                <div role="alert" className="mb-3 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                  <p className="leading-relaxed">{error}</p>
                  <a
                    href={contactHref}
                    className="mt-2 inline-flex items-center gap-1.5 font-bold text-red-800 underline"
                  >
                    <Mail className="h-3.5 w-3.5" /> {t('pay.contactUs')} — {BRAND.email}
                  </a>
                </div>
              )}

              {/* MEMBERSHIPS ONLY — hosted checkout page. */}
              <button
                type="button"
                onClick={() => goToCheckout(planCode)}
                disabled={!ack || busy}
                style={{ touchAction: 'manipulation' }}
                className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-3.5 text-base font-bold text-white transition-colors hover:bg-orange-600 focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-300 disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                {busy ? t('pay.processing') : t('pay.continueCheckout')}
                {!busy && <ExternalLink className="h-4 w-4" />}
              </button>

              {/* Immediate, announced feedback on every tap — desktop AND mobile. */}
              <p aria-live="polite" className="mt-2 min-h-[16px] text-center text-xs font-semibold text-slate-500">
                {busy ? t('pay.openingCheckout') : ''}
              </p>

              <p className="mt-1 flex items-center justify-center gap-1.5 text-xs text-slate-400">
                <ShieldCheck className="h-3.5 w-3.5" /> {t('pay.securedBy')}
              </p>
            </>
          ) : (
            plansHref && (
              <Link
                to={plansHref}
                onClick={onClose}
                className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3.5 text-base font-bold text-white hover:bg-slate-800"
              >
                {t('pay.seePlans')} <ArrowRight className="h-4 w-4" />
              </Link>
            )
          )}

          {/* A membership is always one tap away from the $29 form. */}
          {canBuySingle && plansHref && (
            <Link
              to={plansHref}
              onClick={onClose}
              className="mt-3 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl border-2 border-slate-300 px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50"
            >
              {t('pay.seePlans')} <ArrowRight className="h-4 w-4" />
            </Link>
          )}

        </div>



        {/* Hesitating at the payment step? One tap to a human on WhatsApp. */}
        <div className="mt-5 flex flex-col items-center gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-between">
          <p className="text-xs font-semibold text-slate-500">{t('cta.questions')}</p>
          <a
            href={whatsappHref(lang)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              track('cta_click', { action: 'whatsapp', channel: 'checkout-modal', plan: planCode || kind })
            }
            className="inline-flex items-center gap-1.5 text-sm font-bold text-[#128C4A] hover:underline"
          >
            <MessageCircle className="h-4 w-4 text-[#25D366]" />
            {t('cta.chatNow')}
          </a>
        </div>

      </div>
    </div>
  );
};

export default CheckoutModal;
